import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import EditIcon from "@material-ui/icons/Edit";

import ColorPalettePill from "src/components/ColorPalettePill";
import {
  defaultLightPalette,
  defaultDarkPalette,
} from "src/theme";
import { useThemeContext, useCustomPalette as useCustomPaletteHook } from "src/contexts/ThemeContext";
import EditColorsDialog from "./EditColorsDialog";
import { useRecordEvent } from "src/hooks/useRecordEvent";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(2),
  },
  themeCard: {
    flex: 1,
    cursor: "pointer",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    border: `2px solid transparent`,
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
  },
  themeCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.palette.primary.main,
    "&:hover": {
      borderColor: theme.palette.primary.light
    },
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.body1.fontSize,
  },
  cardSubtitle: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  previewGrid: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  previewBox: {
    flex: 1,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  previewLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
  },
  editButton: {
    marginTop: theme.spacing(1),
  },
  modeToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    marginTop: theme.spacing(2),
  },
  modeLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  modeToggleLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    marginRight: theme.spacing(1),
  },
}));

export function AppearanceSettings() {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const { mode, setMode } = useThemeContext();
  const {
    useCustomPalette,
    customLightPalette,
    customDarkPalette,
    setUseCustomPalette,
  } = useCustomPaletteHook();
  
  const [editColorsOpen, setEditColorsOpen] = useState(false);

  const handleThemeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const isCustom = value === "custom";
    if (isCustom !== useCustomPalette) {
      setUseCustomPalette(isCustom);
      recordEvent({
        type: "feature-adoption",
        feature: "palette-type-toggle",
        paletteType: isCustom ? "custom" : "default",
      });
    }
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as "light" | "dark";
    setMode(newMode);
    recordEvent({
      type: "feature-adoption",
      feature: "theme-mode-toggle",
      element: "appearance-settings",
      mode: newMode,
    });
  };

  return (
    <Box>
      <RadioGroup
        value={useCustomPalette ? "custom" : "default"}
        onChange={handleThemeTypeChange}
      >
        <div className={classes.container}>
          {/* Default Theme Card */}
          <Card
            className={`${classes.themeCard} ${
              !useCustomPalette ? classes.themeCardSelected : ""
            }`}
            onClick={() => {
              if (useCustomPalette) {
                setUseCustomPalette(false);
                recordEvent({
                  type: "feature-adoption",
                  feature: "palette-type-toggle",
                  paletteType: "default",
                });
              }
            }}
          >
            <CardContent>
              <div className={classes.cardHeader}>
                <div>
                  <Typography className={classes.cardTitle}>
                    Default Theme
                  </Typography>
                  <Typography className={classes.cardSubtitle}>
                    Standard app colors
                  </Typography>
                </div>
                <FormControlLabel
                  value="default"
                  control={<Radio color="primary" />}
                  label=""
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className={classes.previewGrid}>
                <div
                  className={classes.previewBox}
                  style={{ backgroundColor: defaultLightPalette.bg }}
                >
                  <div
                    className={classes.previewLabel}
                    style={{ color: defaultLightPalette.text }}
                  >
                    <Brightness7Icon style={{ fontSize: 14 }} />
                    Light
                  </div>
                  <ColorPalettePill
                    palette={defaultLightPalette}
                    size="small"
                    interactive={false}
                    showBrandColors={false}
                  />
                </div>
                <div
                  className={classes.previewBox}
                  style={{ backgroundColor: defaultDarkPalette.bg }}
                >
                  <div
                    className={classes.previewLabel}
                    style={{ color: defaultDarkPalette.text }}
                  >
                    <Brightness4Icon style={{ fontSize: 14 }} />
                    Dark
                  </div>
                  <ColorPalettePill
                    palette={defaultDarkPalette}
                    size="small"
                    interactive={false}
                    showBrandColors={false}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custom Theme Card */}
          <Card
            className={`${classes.themeCard} ${
              useCustomPalette ? classes.themeCardSelected : ""
            }`}
            onClick={() => {
              if (!useCustomPalette) {
                setUseCustomPalette(true);
                recordEvent({
                  type: "feature-adoption",
                  feature: "palette-type-toggle",
                  paletteType: "custom",
                });
              }
            }}
          >
            <CardContent>
              <div className={classes.cardHeader}>
                <div>
                  <Typography className={classes.cardTitle}>
                    Custom Theme
                  </Typography>
                  <Typography className={classes.cardSubtitle}>
                    Personalized colors
                  </Typography>
                </div>
                <FormControlLabel
                  value="custom"
                  control={<Radio color="primary" />}
                  label=""
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className={classes.previewGrid}>
                <div
                  className={classes.previewBox}
                  style={{ backgroundColor: customLightPalette.bg }}
                >
                  <div
                    className={classes.previewLabel}
                    style={{ color: customLightPalette.text }}
                  >
                    <Brightness7Icon style={{ fontSize: 14 }} />
                    Light
                  </div>
                  <ColorPalettePill
                    palette={customLightPalette}
                    size="small"
                    interactive={false}
                    showBrandColors={false}
                  />
                </div>
                <div
                  className={classes.previewBox}
                  style={{ backgroundColor: customDarkPalette.bg }}
                >
                  <div
                    className={classes.previewLabel}
                    style={{ color: customDarkPalette.text }}
                  >
                    <Brightness4Icon style={{ fontSize: 14 }} />
                    Dark
                  </div>
                  <ColorPalettePill
                    palette={customDarkPalette}
                    size="small"
                    interactive={false}
                    showBrandColors={false}
                  />
                </div>
              </div>

              <Button
                className={classes.editButton}
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<EditIcon />}
                disabled={!useCustomPalette}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditColorsOpen(true);
                }}
              >
                Edit Colors
              </Button>
            </CardContent>
          </Card>
        </div>
      </RadioGroup>

      {/* Mode Toggle */}
      <Box className={classes.modeToggle}>
        <Typography variant="body2" className={classes.modeToggleLabel}>
          Current Mode:
        </Typography>
        <RadioGroup
          row
          value={mode}
          onChange={handleModeChange}
        >
          <FormControlLabel
            value="light"
            control={<Radio color="primary" size="small" />}
            label={
              <span className={classes.modeLabel}>
                <Brightness7Icon style={{ fontSize: 16 }} />
                Light
              </span>
            }
          />
          <FormControlLabel
            value="dark"
            control={<Radio color="primary" size="small" />}
            label={
              <span className={classes.modeLabel}>
                <Brightness4Icon style={{ fontSize: 16 }} />
                Dark
              </span>
            }
          />
        </RadioGroup>
      </Box>

      {/* Edit Colors Dialog */}
      <EditColorsDialog
        open={editColorsOpen}
        onClose={() => setEditColorsOpen(false)}
      />
    </Box>
  );
}

export default AppearanceSettings;

